import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import type { ClientDepartures } from '../../../utils/panorama/panoramaClientDepartures';
import type { Status } from '../../../utils/panorama/panoramaPeopleRows';
import { CHART_COLORS } from '../vacationOverviewPage.definitions';
import { ChartTooltip } from './ChartTooltip';
import { EmptyChart } from './DashboardCard';
import { useCompactChart } from './useCompactChart';

interface ClientDeparturesChartProps {
	clients: ClientDepartures[];
	onClientClick?: (clientName: string, statuses: Status[]) => void;
}

export function ClientDeparturesChart({ clients, onClientClick }: ClientDeparturesChartProps) {
	const { isCompact } = useCompactChart();

	if (clients.length === 0) {
		return <EmptyChart message="Ninguém de férias nem saindo no período." height={260} />;
	}

	const chartHeight = 520;

	const handleOnVacationClick = (entry: ClientDepartures) => {
		onClientClick?.(entry.clientName, ['on-vacation']);
	};

	const handleScheduledClick = (entry: ClientDepartures) => {
		onClientClick?.(entry.clientName, ['scheduled']);
	};

	const handlePendingClick = (entry: ClientDepartures) => {
		onClientClick?.(entry.clientName, ['pending', 'approval-urgent']);
	};

	interface CustomTooltipProps {
		active?: boolean;
		payload?: Array<{ name?: string; value?: number; color?: string }>;
		label?: string;
	}

	function CustomTooltip({ active, payload, label }: CustomTooltipProps) {
		if (!active || !payload || payload.length === 0) return null;

		const dataPoint = clients.find(d => d.clientName === label);
		if (!dataPoint) return null;

		const percent = dataPoint.teamSize > 0 ? Number(((dataPoint.total / dataPoint.teamSize) * 100).toFixed(1)) : 0;

		return (
			<div className="rounded-lg border border-raro-gray-200 bg-white p-3 text-xs shadow-lg">
				<p className="mb-2 font-semibold text-raro-gray-900">{label}</p>
				{payload.map((entry, idx) => (
					<p key={`${entry.name}-${idx}`} className="flex items-center gap-2 text-raro-gray-700">
						<span className="h-2 w-2 rounded-full" style={{ backgroundColor: entry.color }} />
						<span>{entry.name}:</span>
						<span className="font-semibold">{entry.value} pessoa{(entry.value ?? 0) !== 1 ? 's' : ''}</span>
					</p>
				))}
				<p className="mt-2 border-t border-raro-gray-200 pt-2 font-semibold text-raro-gray-900">
					{dataPoint.total} de {dataPoint.teamSize} pessoas ({percent}%)
				</p>
			</div>
		);
	}

	return (
		<div>
			<div style={{ height: chartHeight }}>
				<ResponsiveContainer width="100%" height="100%">
					<BarChart
						data={clients}
						layout="vertical"
						margin={{ top: 8, right: 16, left: 0, bottom: 0 }}
						barGap={2}
					>
						<CartesianGrid stroke={CHART_COLORS.grid} vertical={true} horizontal={false} />
						<XAxis type="number" allowDecimals={false} tick={{ fontSize: 11, fill: CHART_COLORS.axis }} axisLine={false} tickLine={false} />
						<YAxis
							dataKey="clientName"
							type="category"
							width={isCompact ? 100 : 150}
							tick={{ fontSize: 11, fill: CHART_COLORS.axis }}
							axisLine={false}
							tickLine={false}
						/>
						<Tooltip content={<CustomTooltip />} cursor={{ fill: '#f1f5f9' }} />
						<Bar
							dataKey="onVacation"
							name="Em férias"
							fill="#0d9488"
							barSize={10}
							onClick={data => onClientClick && handleOnVacationClick(data.payload)}
							cursor={onClientClick ? 'pointer' : 'default'}
							radius={[0, 2, 2, 0]}
						/>
						<Bar
							dataKey="scheduled"
							name="Agendados"
							fill="#0284c7"
							barSize={10}
							onClick={data => onClientClick && handleScheduledClick(data.payload)}
							cursor={onClientClick ? 'pointer' : 'default'}
							radius={[0, 2, 2, 0]}
						/>
						<Bar
							dataKey="pending"
							name="Em aprovação"
							fill="#7c3aed"
							barSize={10}
							onClick={data => onClientClick && handlePendingClick(data.payload)}
							cursor={onClientClick ? 'pointer' : 'default'}
							radius={[0, 2, 2, 0]}
						/>
					</BarChart>
				</ResponsiveContainer>
			</div>
			<div className="flex justify-center gap-4 pt-3 text-xs">
				<div className="flex items-center gap-2">
					<span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: '#0d9488' }} />
					<span className="text-raro-gray-600">Em férias</span>
				</div>
				<div className="flex items-center gap-2">
					<span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: '#0284c7' }} />
					<span className="text-raro-gray-600">Agendados</span>
				</div>
				<div className="flex items-center gap-2">
					<span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: '#7c3aed' }} />
					<span className="text-raro-gray-600">Em aprovação</span>
				</div>
			</div>
		</div>
	);
}
